import { devError } from '@/lib/logger';
import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Download, CalendarIcon, BarChart3, Table, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  working: '#059669',
  on_break: '#F59E0B',
  completed: '#111111',
  not_started: '#9CA3AF',
};

const CHART_TICK = { fontSize: 11 };
const CHART_GRID_STROKE = "#E5E7EB";

export default function Reports() {
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState('table');

  useEffect(() => {
    fetchLocations();
    fetchEmployees();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRecords();
  }, [selectedLocation, selectedEmployee, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`, { withCredentials: true });
      setLocations(res.data);
    } catch (err) { devError("reports", err);
      // Silent
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees`, { withCredentials: true });
      setEmployees(res.data);
    } catch (err) { devError("reports", err);
      // Silent
    }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      let url = `${API}/time-records?`;
      url += `start_date=${format(startDate, 'yyyy-MM-dd')}&`;
      url += `end_date=${format(endDate, 'yyyy-MM-dd')}`;
      
      if (selectedLocation !== 'all') url += `&location_id=${selectedLocation}`;
      if (selectedEmployee !== 'all') url += `&employee_id=${selectedEmployee}`;

      const res = await axios.get(url, { withCredentials: true });
      setRecords(res.data);
    } catch (error) {
      toast.error('Erro ao carregar registros');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      let url = `${API}/reports/excel?report_type=location`;
      url += `&start_date=${format(startDate, 'yyyy-MM-dd')}`;
      url += `&end_date=${format(endDate, 'yyyy-MM-dd')}`;
      if (selectedLocation !== 'all') url += `&location_id=${selectedLocation}`;
      if (selectedEmployee !== 'all') url += `&employee_id=${selectedEmployee}`;

      const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      
      const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `relatorio_ponto_${format(new Date(), 'yyyyMMdd')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Relatório baixado');
    } catch (error) {
      toast.error('Erro ao baixar relatório');
    }
  };

  const setQuickRange = (range) => {
    const today = new Date();
    if (range === 'today') { setStartDate(today); setEndDate(today); }
    else if (range === 'week') { setStartDate(subDays(today, 7)); setEndDate(today); }
    else if (range === 'month') { setStartDate(subMonths(today, 1)); setEndDate(today); }
  };

  const getEmployeeName = (id) => employees.find(e => e.employee_id === id)?.name || '-';
  const getLocationName = (id) => locations.find(l => l.location_id === id)?.name || '-';
  const formatTime = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); }
    catch { return '-'; }
  };

  // Chart data
  const totalNetHours = records.reduce((sum, r) => sum + (r.net_hours || 0), 0);
  const totalNetDisplay = (() => {
    const h = Math.floor(totalNetHours);
    const m = Math.round((totalNetHours - h) * 60);
    return `${h}h${String(m).padStart(2, '0')}`;
  })();

  const statusCounts = {
    completed: records.filter(r => r.status === 'completed').length,
    working: records.filter(r => r.status === 'working').length,
    on_break: records.filter(r => r.status === 'on_break').length,
    not_started: records.filter(r => r.status === 'not_started').length,
  };

  const pieData = [
    { name: 'Concluído', value: statusCounts.completed, color: '#111111' },
    { name: 'Trabalhando', value: statusCounts.working, color: '#059669' },
    { name: 'Em Pausa', value: statusCounts.on_break, color: '#F59E0B' },
    { name: 'Não Iniciado', value: statusCounts.not_started, color: '#9CA3AF' },
  ].filter(d => d.value > 0);

  // Per-employee summary
  const employeeSummary = {};
  records.forEach(r => {
    if (!employeeSummary[r.employee_id]) {
      employeeSummary[r.employee_id] = { name: getEmployeeName(r.employee_id), total: 0, late: 0, completed: 0, netHours: 0 };
    }
    employeeSummary[r.employee_id].total++;
    employeeSummary[r.employee_id].netHours += (r.net_hours || 0);
    if (r.is_late) employeeSummary[r.employee_id].late++;
    if (r.status === 'completed') employeeSummary[r.employee_id].completed++;
  });
  const barData = Object.values(employeeSummary).map(e => ({
    ...e,
    netHours: Math.round(e.netHours * 10) / 10
  }));

  return (
    <AdminLayout title="Relatórios">
      {/* Filters */}
      <div className="border border-[#E5E7EB] p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block">Local</Label>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger data-testid="report-location-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Locais</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block">Funcionário</Label>
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger data-testid="report-employee-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Funcionários</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.employee_id} value={emp.employee_id}>{emp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block">Data Início</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start" data-testid="start-date-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />{format(startDate, 'dd/MM/yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)} locale={ptBR} />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block">Data Fim</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start" data-testid="end-date-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />{format(endDate, 'dd/MM/yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={endDate} onSelect={(d) => d && setEndDate(d)} locale={ptBR} />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Label className="text-xs uppercase tracking-widest">Período Rápido:</Label>
          <Button variant="outline" size="sm" onClick={() => setQuickRange('today')} data-testid="quick-today">Hoje</Button>
          <Button variant="outline" size="sm" onClick={() => setQuickRange('week')} data-testid="quick-week">Última Semana</Button>
          <Button variant="outline" size="sm" onClick={() => setQuickRange('month')} data-testid="quick-month">Último Mês</Button>
          <div className="ml-auto">
            <Button onClick={downloadExcel} data-testid="download-excel-btn">
              <Download className="w-4 h-4 mr-2" />Baixar Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {records.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard value={records.length} label="Total Registros" />
          <SummaryCard value={records.filter(r => r.status === 'completed').length} label="Completos" color="#059669" />
          <SummaryCard value={records.filter(r => r.is_late).length} label="Atrasos" color="#DC2626" />
          <SummaryCard value={new Set(records.map(r => r.employee_id)).size} label="Funcionários" />
        </div>
      )}

      {/* Tabs: Table / Charts / Banco */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="table" data-testid="tab-table">
            <Table className="w-4 h-4 mr-2" />Registros & Banco de Horas
          </TabsTrigger>
          <TabsTrigger value="charts" data-testid="tab-charts">
            <BarChart3 className="w-4 h-4 mr-2" />Gráficos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="table">
          {/* Banco de Horas - Summary */}
          {records.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs uppercase tracking-widest text-[#6B7280] mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Banco de Horas
              </h3>
              <BancoDeHoras
                startDate={format(startDate, 'yyyy-MM-dd')}
                endDate={format(endDate, 'yyyy-MM-dd')}
                locationId={selectedLocation !== 'all' ? selectedLocation : null}
                employeeId={selectedEmployee !== 'all' ? selectedEmployee : null}
              />
            </div>
          )}

          {/* Detailed Records */}
          <h3 className="text-xs uppercase tracking-widest text-[#6B7280] mb-3">Registros Detalhados</h3>
          <div className="border border-[#E5E7EB] overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th><th>Funcionário</th><th>Local</th><th>Entrada</th>
                  <th>Início Pausa</th><th>Fim Pausa</th><th>Saída</th>
                  <th>Horas Brutas</th><th>Pausa</th><th>Horas Líquidas</th>
                  <th>Status</th><th>Atrasado</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-[#111111] border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </td></tr>
                ) : records.length === 0 ? (
                  <tr><td colSpan={12} className="text-center py-8 text-[#6B7280]">Nenhum registro encontrado</td></tr>
                ) : (
                  records.map(record => (
                    <tr key={record.record_id}>
                      <td className="mono">{record.date}</td>
                      <td>{getEmployeeName(record.employee_id)}</td>
                      <td>{getLocationName(record.location_id)}</td>
                      <td className="mono">{formatTime(record.clock_in)}</td>
                      <td className="mono">{formatTime(record.break_start)}</td>
                      <td className="mono">{formatTime(record.break_end)}</td>
                      <td
                        className={`mono ${record.is_auto_closed ? 'text-[#DC2626] font-bold' : ''}`}
                        title={record.is_auto_closed ? 'Fechamento compulsório' : ''}
                      >
                        {formatTime(record.clock_out)}
                        {record.is_auto_closed ? ' *' : ''}
                      </td>
                      <td className="mono font-semibold">{record.worked_display || '-'}</td>
                      <td className="mono text-[#F59E0B]">{record.break_display || '-'}</td>
                      <td className="mono font-bold text-[#0029FF]">{record.net_display || '-'}</td>
                      <td><StatusBadge status={record.status} /></td>
                      <td>{record.is_late ? <span className="text-[#DC2626] font-semibold">Sim</span> : <span className="text-[#6B7280]">Não</span>}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="charts">
          {records.length === 0 ? (
            <div className="text-center py-16 border border-[#E5E7EB]">
              <p className="text-[#6B7280]">Nenhum dado para exibir gráficos</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Status Distribution Pie */}
              <div className="border border-[#E5E7EB] p-6">
                <h3 className="text-xs uppercase tracking-widest text-[#6B7280] mb-4">Distribuição por Status</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, value }) => `${name}: ${value}`}>
                      {pieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Per Employee Bar Chart - Hours */}
              <div className="border border-[#E5E7EB] p-6">
                <h3 className="text-xs uppercase tracking-widest text-[#6B7280] mb-4">Horas por Funcionário</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                    <XAxis dataKey="name" tick={CHART_TICK} />
                    <YAxis tick={CHART_TICK} unit="h" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="netHours" name="Horas Líquidas" fill="#0029FF" />
                    <Bar dataKey="late" name="Atrasos" fill="#DC2626" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}

function SummaryCard({ value, label, color }) {
  return (
    <div className="border border-[#E5E7EB] p-4 text-center">
      <div className="text-3xl font-black" style={{ color: color || '#111111', fontFamily: 'Satoshi, sans-serif' }}>
        {value}
      </div>
      <div className="text-xs uppercase tracking-widest text-[#6B7280]">{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const labels = { not_started: 'Não Iniciado', working: 'Trabalhando', on_break: 'Em Pausa', completed: 'Concluído' };
  const colors = { not_started: 'bg-[#9CA3AF]', working: 'bg-[#059669]', on_break: 'bg-[#F59E0B]', completed: 'bg-[#111111]' };
  return <span className={`badge ${colors[status]} text-white`}>{labels[status]}</span>;
}

function BancoDeHoras({ startDate, endDate, locationId, employeeId }) {
  const [bancoData, setBancoData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBanco();
  }, [startDate, endDate, locationId, employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBanco = async () => {
    setLoading(true);
    try {
      let url = `${API}/time-records/banco-horas?`;
      if (startDate) url += `start_date=${startDate}&`;
      if (endDate) url += `end_date=${endDate}&`;
      if (locationId) url += `location_id=${locationId}&`;
      if (employeeId) url += `employee_id=${employeeId}`;
      
      const res = await axios.get(url, { withCredentials: true });
      setBancoData(res.data);
    } catch (err) { devError("reports", err);
      // Silent
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-6 h-6 border-2 border-[#111111] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (bancoData.length === 0) {
    return (
      <div className="text-center py-16 border border-[#E5E7EB]">
        <p className="text-[#6B7280]">Nenhum dado de banco de horas</p>
      </div>
    );
  }

  return (
    <div className="border border-[#E5E7EB] overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Funcionário</th>
            <th>Dias Trabalhados</th>
            <th>Horas Esperadas</th>
            <th>Horas Trabalhadas</th>
            <th>Desc. Almoço</th>
            <th>Atrasos</th>
            <th>Banco de Horas</th>
          </tr>
        </thead>
        <tbody>
          {bancoData.map(row => (
            <tr key={row.employee_id} data-testid={`banco-row-${row.employee_id}`}>
              <td>
                <div className="font-medium">{row.name}</div>
                <div className="text-xs text-[#6B7280]">{row.email}</div>
              </td>
              <td className="mono text-center">{row.total_days}</td>
              <td className="mono text-center">{row.total_expected_hours}h</td>
              <td className="mono text-center font-bold text-[#0029FF]">{row.total_net_hours}h</td>
              <td className="mono text-center text-[#6B7280]">{row.total_lunch_deduction}h</td>
              <td className="mono text-center">{row.total_late > 0 ? <span className="text-[#DC2626]">{row.total_late}</span> : '0'}</td>
              <td className="mono text-center">
                <span className={`text-lg font-black ${row.banco_horas >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
                  {row.banco_display}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
